import type { HotelRow, RoomTypeRow } from '../types'
import { ALLOCATIONS_HEADERS, COMPANIES_HEADERS, GUESTS_HEADERS, findSheet, ParsedSheet } from './shared'

export interface ImportIssue {
  sheet: string
  row: number
  severity: 'warning' | 'error'
  message: string
}

export interface UnresolvedRef {
  key: string // lowercased name as it appeared in the file
  originalName: string
  kind: 'hotel' | 'room_type'
  /** admin's resolution: map to an existing id, or approve creating a new one, or leave unresolved (blocks apply) */
  resolution: { type: 'existing'; id: string } | { type: 'create' } | { type: 'unresolved' }
}

export interface PreviewCompany {
  code: string
  name: string
  contact_person: string | null
  contact_mobile: string | null
  contact_email: string | null
  notes: string | null
  deadline_at: string | null
  status: string | null
  isNew: boolean
}

export interface PreviewAllocation {
  row: number
  company_code: string
  hotel_name: string
  room_type_name: string
  room_count: number
  capacity_per_room: number
  check_in: string | null
  check_out: string | null
  notes: string | null
}

export interface PreviewGuest {
  row: number
  company_code: string
  hotel_name: string
  room_type_name: string
  room_slot: number
  guest_slot: number
  full_name: string
  mobile: string
  email: string | null
  check_in: string | null
  check_out: string | null
  notes: string | null
  guest_id: string | null
}

export interface ImportPreview {
  companies: PreviewCompany[]
  allocations: PreviewAllocation[]
  guests: PreviewGuest[]
  unresolved: UnresolvedRef[]
  issues: ImportIssue[]
  stats: {
    companies: number
    hotels: number
    allocations: number
    rooms: number
    guestCapacity: number
    guests: number
  }
}

function req(headers: string[], expected: string[], sheetName: string, issues: ImportIssue[]) {
  for (const h of expected) {
    if (!headers.includes(h)) {
      issues.push({ sheet: sheetName, row: 1, severity: 'error', message: `Missing required column "${h}"` })
    }
  }
}

function parseIntSafe(v: string): number | null {
  const n = parseInt((v || '').replace(/[^0-9-]/g, ''), 10)
  return Number.isFinite(n) ? n : null
}

function parseDateSafe(v: string): string | null {
  if (!v) return null
  const d = new Date(v)
  if (isNaN(d.getTime())) return null
  return d.toISOString().slice(0, 10)
}

export function buildImportPreview(
  sheets: ParsedSheet[],
  existing: { hotels: HotelRow[]; roomTypes: RoomTypeRow[]; companyCodes: Set<string> }
): ImportPreview {
  const issues: ImportIssue[] = []
  const companiesSheet = findSheet(sheets, 'Companies')
  const allocationsSheet = findSheet(sheets, 'Allocations')
  const guestsSheet = findSheet(sheets, 'Guests')

  if (!companiesSheet && !allocationsSheet) {
    issues.push({ sheet: 'workbook', row: 0, severity: 'error', message: 'No "Companies" or "Allocations" sheet found. Use the downloadable template.' })
  }
  if (companiesSheet) req(companiesSheet.headers, COMPANIES_HEADERS.slice(0, 2), 'Companies', issues)
  if (allocationsSheet) req(allocationsSheet.headers, ALLOCATIONS_HEADERS.slice(0, 5), 'Allocations', issues)
  if (guestsSheet) req(guestsSheet.headers, GUESTS_HEADERS.slice(0, 6), 'Guests', issues)

  const hotelByName = new Map(existing.hotels.map((h) => [h.name.toLowerCase(), h]))
  const roomTypeByName = new Map(existing.roomTypes.map((r) => [r.name.toLowerCase(), r]))
  const unresolvedMap = new Map<string, UnresolvedRef>()

  function resolveRef(name: string, kind: 'hotel' | 'room_type') {
    const key = `${kind}:${name.toLowerCase()}`
    const known = kind === 'hotel' ? hotelByName.has(name.toLowerCase()) : roomTypeByName.has(name.toLowerCase())
    if (!known && !unresolvedMap.has(key)) {
      unresolvedMap.set(key, { key, originalName: name, kind, resolution: { type: 'unresolved' } })
    }
  }

  // --- Companies ---
  const companies: PreviewCompany[] = []
  const seenCodes = new Set<string>()
  ;(companiesSheet?.rows || []).forEach((r, idx) => {
    const rowNum = idx + 2
    const code = (r['Company Code'] || '').toUpperCase()
    if (!code) {
      issues.push({ sheet: 'Companies', row: rowNum, severity: 'error', message: 'Missing Company Code' })
      return
    }
    if (seenCodes.has(code)) {
      issues.push({ sheet: 'Companies', row: rowNum, severity: 'error', message: `Duplicate Company Code "${code}" within this file` })
    }
    seenCodes.add(code)
    let deadlineAt: string | null = null
    if (r['Deadline']) {
      const d = new Date(r['Deadline'])
      if (isNaN(d.getTime())) issues.push({ sheet: 'Companies', row: rowNum, severity: 'warning', message: `Could not parse Deadline "${r['Deadline']}"` })
      else deadlineAt = d.toISOString()
    }
    const status = (r['Portal Status'] || '').toLowerCase().replace(' ', '_') || null
    if (status && !['draft', 'open', 'edit_only', 'closed'].includes(status)) {
      issues.push({ sheet: 'Companies', row: rowNum, severity: 'warning', message: `Unknown Portal Status "${r['Portal Status']}", ignoring` })
    }
    companies.push({
      code,
      name: r['Company Name'] || code,
      contact_person: r['Contact Person'] || null,
      contact_mobile: r['Contact Mobile'] || null,
      contact_email: r['Contact Email'] || null,
      notes: r['Notes'] || null,
      deadline_at: deadlineAt,
      status: status && ['draft', 'open', 'edit_only', 'closed'].includes(status) ? status : null,
      isNew: !existing.companyCodes.has(code),
    })
  })

  // --- Allocations ---
  const allocations: PreviewAllocation[] = []
  ;(allocationsSheet?.rows || []).forEach((r, idx) => {
    const rowNum = idx + 2
    const code = (r['Company Code'] || '').toUpperCase()
    const hotelName = r['Hotel'] || ''
    const roomTypeName = r['Room Type'] || ''
    if (!code || !hotelName || !roomTypeName) {
      issues.push({ sheet: 'Allocations', row: rowNum, severity: 'error', message: 'Company Code, Hotel and Room Type are all required' })
      return
    }
    if (!seenCodes.has(code) && !existing.companyCodes.has(code)) {
      issues.push({ sheet: 'Allocations', row: rowNum, severity: 'error', message: `Company Code "${code}" is not in the Companies sheet or the event` })
    }
    resolveRef(hotelName, 'hotel')
    resolveRef(roomTypeName, 'room_type')

    const roomCount = parseIntSafe(r['Room Count'])
    const capacity = parseIntSafe(r['Capacity Per Room'])
    if (roomCount === null || roomCount < 0) issues.push({ sheet: 'Allocations', row: rowNum, severity: 'error', message: `Invalid Room Count "${r['Room Count']}"` })
    if (capacity === null || capacity < 1) issues.push({ sheet: 'Allocations', row: rowNum, severity: 'error', message: `Invalid Capacity Per Room "${r['Capacity Per Room']}"` })

    allocations.push({
      row: rowNum,
      company_code: code,
      hotel_name: hotelName,
      room_type_name: roomTypeName,
      room_count: roomCount ?? 0,
      capacity_per_room: capacity ?? 1,
      check_in: parseDateSafe(r['Check-in']),
      check_out: parseDateSafe(r['Check-out']),
      notes: r['Notes'] || null,
    })
  })

  // --- Guests ---
  const guests: PreviewGuest[] = []
  const guestMobiles = new Map<string, number>()
  ;(guestsSheet?.rows || []).forEach((r, idx) => {
    const rowNum = idx + 2
    const code = (r['Company Code'] || '').toUpperCase()
    const hotelName = r['Hotel'] || ''
    const roomTypeName = r['Room Type'] || ''
    const name = r['Guest Name'] || ''
    const mobile = r['Mobile'] || ''
    if (!name && !mobile) return // fully blank guest row -- skip silently

    if (!code || !hotelName || !roomTypeName) {
      issues.push({ sheet: 'Guests', row: rowNum, severity: 'error', message: 'Company Code, Hotel and Room Type are all required' })
      return
    }
    if ((name && !mobile) || (!name && mobile)) {
      issues.push({ sheet: 'Guests', row: rowNum, severity: 'error', message: 'A guest needs both Guest Name and Mobile, or neither' })
    }
    resolveRef(hotelName, 'hotel')
    resolveRef(roomTypeName, 'room_type')

    const roomSlot = parseIntSafe(r['Room Slot'])
    const guestSlot = parseIntSafe(r['Guest Slot'])
    if (!roomSlot || roomSlot < 1) issues.push({ sheet: 'Guests', row: rowNum, severity: 'error', message: `Invalid Room Slot "${r['Room Slot']}"` })
    if (!guestSlot || guestSlot < 1) issues.push({ sheet: 'Guests', row: rowNum, severity: 'error', message: `Invalid Guest Slot "${r['Guest Slot']}"` })

    const normalizedMobile = mobile.replace(/\D/g, '')
    if (normalizedMobile) {
      if (guestMobiles.has(normalizedMobile)) {
        issues.push({ sheet: 'Guests', row: rowNum, severity: 'warning', message: `Mobile "${mobile}" also appears on row ${guestMobiles.get(normalizedMobile)} in this file` })
      } else {
        guestMobiles.set(normalizedMobile, rowNum)
      }
    }

    guests.push({
      row: rowNum,
      company_code: code,
      hotel_name: hotelName,
      room_type_name: roomTypeName,
      room_slot: roomSlot ?? 0,
      guest_slot: guestSlot ?? 0,
      full_name: name,
      mobile,
      email: r['Email'] || null,
      check_in: parseDateSafe(r['Check-in']),
      check_out: parseDateSafe(r['Check-out']),
      notes: r['Notes'] || null,
      guest_id: r['Guest ID (internal)'] || null,
    })
  })

  const guestCapacity = allocations.reduce((s, a) => s + a.room_count * a.capacity_per_room, 0)
  const hotelNames = new Set(allocations.map((a) => a.hotel_name.toLowerCase()))

  return {
    companies,
    allocations,
    guests,
    unresolved: Array.from(unresolvedMap.values()),
    issues,
    stats: {
      companies: companies.length,
      hotels: hotelNames.size,
      allocations: allocations.length,
      rooms: allocations.reduce((s, a) => s + a.room_count, 0),
      guestCapacity,
      guests: guests.filter((g) => g.full_name).length,
    },
  }
}
